"use client";

import { WebViewerInstance } from "@pdftron/webviewer";
import { useEffect, useState, useRef } from "react";

import { usePubSub, useMeeting } from "../../config/sdkConfig";

import { useSiteContext } from "../../context/SiteContext";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import useIsMobile from "../../hooks/useIsMobile";
export default function DocumentViewer({
  fileUrl,
  onClosePanel,
  sideBarContainerWidth,
}) {
  const { sideBarMode } = useMeetingAppContext();
  const viewer = useRef(null);
  const webviewerInstanceRef = useRef(null);
  const [webviewerInstance, setWebViewerInstance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const isApplyingRemoteChange = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const resizeHandlerRef = useRef(null);
  const isDocumentLoaded = useRef(false);
  const queuedAnnotations = useRef(null);
  const { meetingRole } = useSiteContext();
  const isMobile = useIsMobile();

  const mMeeting = useMeeting();

  const { publish } = usePubSub("SHOW_DOCUMENT_SECTION", {
    onMessageReceived: (data) => {
      const instance = webviewerInstanceRef.current;
      if (!instance || !data?.message) return;

      // Don't process our own events
      if (data.senderId === mMeeting?.localParticipant?.id) {
        return;
      }

      console.log("Received SHOW_DOCUMENT_SECTION event:", data);

      const { documentViewer } = instance.Core;
      const scrollView = documentViewer.getScrollViewElement();
      const { eventName, eventData } = data.message;

      // Set flag to prevent feedback loop
      isApplyingRemoteChange.current = true;

      try {
        switch (eventName) {
          case "pageNumberUpdated":
            if (
              eventData?.pageNumber &&
              documentViewer.getCurrentPage() !== eventData.pageNumber
            ) {
              documentViewer.setCurrentPage(eventData.pageNumber);
            }
            break;

          case "zoomUpdated":
            if (eventData?.zoom) {
              instance.UI.setZoomLevel(eventData.zoom);
            }
            break;

          case "scrollUpdated":
            if (
              eventData?.scrollTop !== undefined &&
              eventData?.scrollLeft !== undefined
            ) {
              scrollView.scrollTop = eventData.scrollTop;
              scrollView.scrollLeft = eventData.scrollLeft;
            }
            break;

          case "fitModeChanged":
            if (eventData?.fitMode) {
              instance.UI.setFitMode(eventData.fitMode);
            }
            break;

          case "annotationChanged":
            if (eventData?.annotations) {
              const { annotationManager: annManager, documentViewer } =
                instance.Core;
              // Check if document is loaded before importing annotations
              if (
                isDocumentLoaded.current &&
                documentViewer.getPageCount() > 0
              ) {
                try {
                  annManager.importAnnotations(eventData.annotations);
                } catch (error) {
                  console.error("Error importing annotations:", error);
                }
              } else {
                // Queue annotations to import after document loads
                queuedAnnotations.current = eventData.annotations;
              }
            }
            break;

          case "layoutChanged":
            if (eventData?.layoutMode) {
              instance.UI.setLayoutMode(eventData.layoutMode);
            }
            break;

          case "toolModeUpdated":
            if (eventData?.toolName) {
              instance.UI.setToolMode(eventData.toolName);
            }
            break;

          case "rotationUpdated":
            console.log("rotationUpdated", instance);
            if (eventData?.rotation && eventData?.pageNumber) {
              // do for all pages
              for (let i = 1; i <= documentViewer.getPageCount(); i++) {
                const originalRotation = documentViewer
                  .getDocument()
                  .getPageRotation(i);
                if (originalRotation !== eventData.rotation) {
                  documentViewer
                    .getDocument()
                    .rotatePages([i], eventData.rotation * 90);
                }
              }
              documentViewer.refreshAll();
              documentViewer.updateView();
            }
            break;

          default:
            console.log("Unknown event:", eventName);
        }
      } finally {
        // Reset flag after a delay to allow event to complete and prevent feedback
        setTimeout(() => {
          isApplyingRemoteChange.current = false;
        }, 500); // Increased delay for rotation/redraw
      }
    },
  });

  const sanitizeUrl = (url) => {
    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    } else if (url.startsWith("https://")) {
      return url;
    } else {
      return `https://${url}`;
    }
  };

  useEffect(() => {
    // Reset document loaded flag when fileUrl changes
    isDocumentLoaded.current = false;
    queuedAnnotations.current = null;

    // Due to SSR in Next.js, we need to import the module dynamically, to avoid window is not defined error due to re-rendering.
    // Read more here: https://github.com/vercel/next.js/discussions/42319
    import("@pdftron/webviewer").then((module) => {
      const WebViewer = module.default;
      WebViewer(
        {
          path: "/lib/webviewer",
          initialDoc: sanitizeUrl(fileUrl),
          licenseKey:
            "R-Link LLC:OEM:a5jOK000000xArN::B+3:AMS(20260828):A773575F761D1145BB535788D2C8F496839C21EDF969F948792B170966FA656D", // sign up to get a free trial key at https://dev.apryse.com
          disableVirtualDisplayMode: true,
          initialLayoutMode: "Single",
          initialDisplayMode: "Continuous", // Presentation mode - continuous scrolling
          width: sideBarMode
            ? `calc(100% - ${sideBarContainerWidth}px)`
            : "100%",
          enableAnnotations: true,
          enableFilePicker: false,
          enableMeasurement: false,
        },
        viewer.current
      ).then((instance) => {
        const { documentViewer, annotationManager, Annotations } =
          instance.Core;
        // Run APIs here.

        // Define hideToolbars function first so it's accessible everywhere
        const hideToolbars = () => {
          const viewerElement = viewer.current;
          if (viewerElement) {
            // Function to hide element and all children
            const hideElement = (el) => {
              if (el) {
                el.style.cssText =
                  "display: none !important; visibility: hidden !important; height: 0 !important; max-height: 0 !important; overflow: hidden !important; padding: 0 !important; margin: 0 !important;";
                el.setAttribute("hidden", "true");
                el.setAttribute("aria-hidden", "true");
              }
            };

            // Check shadow DOM
            const checkShadowDOM = (element) => {
              if (element && element.shadowRoot) {
                const shadowElements = element.shadowRoot.querySelectorAll("*");
                shadowElements.forEach((el) => {
                  const className = el.className?.toString() || "";
                  const id = el.id || "";
                  const dataElement = el.getAttribute("data-element") || "";
                  const tagName = el.tagName?.toLowerCase() || "";

                  if (
                    className.toLowerCase().includes("toolbar") ||
                    className.toLowerCase().includes("header") ||
                    className.toLowerCase().includes("ribbon") ||
                    id.toLowerCase().includes("toolbar") ||
                    id.toLowerCase().includes("header") ||
                    dataElement.toLowerCase().includes("toolbar") ||
                    dataElement.toLowerCase().includes("header") ||
                    tagName === "header" ||
                    tagName === "nav"
                  ) {
                    hideElement(el);
                  }
                });
              }
            };

            // Check all elements for shadow DOM
            const allElementsForShadow = viewerElement.querySelectorAll("*");
            allElementsForShadow.forEach((el) => {
              checkShadowDOM(el);
            });
            checkShadowDOM(viewerElement);

            // Also check iframes if WebViewer uses iframe
            const iframes = viewerElement.querySelectorAll("iframe");
            iframes.forEach((iframe) => {
              try {
                const iframeDoc =
                  iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                  // Check shadow DOM in iframe
                  checkShadowDOM(iframeDoc.documentElement);

                  const iframeElements = iframeDoc.querySelectorAll("*");
                  iframeElements.forEach((el) => {
                    checkShadowDOM(el);
                    const className = el.className?.toString() || "";
                    const id = el.id || "";
                    const dataElement = el.getAttribute("data-element") || "";
                    const tagName = el.tagName?.toLowerCase() || "";

                    if (
                      className.toLowerCase().includes("toolbar") ||
                      className.toLowerCase().includes("header") ||
                      className.toLowerCase().includes("ribbon") ||
                      id.toLowerCase().includes("toolbar") ||
                      id.toLowerCase().includes("header") ||
                      dataElement.toLowerCase().includes("toolbar") ||
                      dataElement.toLowerCase().includes("header") ||
                      tagName === "header" ||
                      tagName === "nav"
                    ) {
                      hideElement(el);
                    }
                  });
                }
              } catch (e) {
                // Cross-origin iframe, can't access
              }
            });
            // Hide all toolbar-related elements with comprehensive selectors
            const toolbarSelectors = [
              ".Toolbar",
              ".toolbar",
              ".Header",
              ".header",
              ".DocumentHeader",
              ".Ribbon",
              ".ribbon",
              ".ToolsHeader",
              ".toolsHeader",
              ".ToolbarGroup",
              ".toolbar-group",
              '[data-element*="toolbar"]',
              '[data-element*="Toolbar"]',
              '[data-element="documentHeader"]',
              '[data-element="toolsHeader"]',
              '[data-element="header"]',
              '[data-element="ribbon"]',
              '[class*="Toolbar"]',
              '[class*="toolbar"]',
              '[class*="Header"]',
              '[class*="header"]',
              '[id*="toolbar"]',
              '[id*="Toolbar"]',
              '[id*="header"]',
              '[id*="Header"]',
              'div[class*="Toolbar"]',
              'div[class*="toolbar"]',
              'div[class*="Header"]',
              'nav[class*="Toolbar"]',
              'nav[class*="toolbar"]',
            ];

            toolbarSelectors.forEach((selector) => {
              try {
                const elements = viewerElement.querySelectorAll(selector);
                elements.forEach((el) => {
                  hideElement(el);
                });
              } catch (e) {
                // Ignore selector errors
              }
            });

            // Also try to find and hide by inspecting all child elements
            // But preserve navigation elements
            const allElements = viewerElement.querySelectorAll("*");
            allElements.forEach((el) => {
              checkShadowDOM(el);
              const className = el.className?.toString() || "";
              const id = el.id || "";
              const dataElement = el.getAttribute("data-element") || "";
              const tagName = el.tagName?.toLowerCase() || "";

              // Don't hide navigation/page controls
              const isNavigation =
                dataElement.toLowerCase().includes("navigation") ||
                dataElement.toLowerCase().includes("page") ||
                dataElement.toLowerCase().includes("prev") ||
                dataElement.toLowerCase().includes("next") ||
                className.toLowerCase().includes("navigation") ||
                className.toLowerCase().includes("pagecontrol") ||
                className.toLowerCase().includes("page-control");

              if (
                !isNavigation &&
                (className.toLowerCase().includes("toolbar") ||
                  className.toLowerCase().includes("header") ||
                  className.toLowerCase().includes("ribbon") ||
                  id.toLowerCase().includes("toolbar") ||
                  id.toLowerCase().includes("header") ||
                  (dataElement.toLowerCase().includes("toolbar") &&
                    !dataElement.toLowerCase().includes("navigation")) ||
                  (dataElement.toLowerCase().includes("header") &&
                    !dataElement.toLowerCase().includes("page")) ||
                  tagName === "header" ||
                  (tagName === "nav" && !isNavigation))
              ) {
                hideElement(el);
              }
            });

            // Try to access WebViewer's internal DOM structure
            try {
              // Check if instance has a getViewerElement or similar method
              if (instance.UI.getViewerElement) {
                const viewerEl = instance.UI.getViewerElement();
                if (viewerEl) {
                  checkShadowDOM(viewerEl);
                  const viewerChildren = viewerEl.querySelectorAll("*");
                  viewerChildren.forEach((el) => {
                    const className = el.className?.toString() || "";
                    const id = el.id || "";
                    if (
                      className.toLowerCase().includes("toolbar") ||
                      className.toLowerCase().includes("header") ||
                      id.toLowerCase().includes("toolbar") ||
                      id.toLowerCase().includes("header")
                    ) {
                      hideElement(el);
                    }
                  });
                }
              }
            } catch (e) {
              // Method not available
            }

            // Debug: Log all elements to help identify toolbar
            console.log("WebViewer elements:", {
              total: viewerElement.querySelectorAll("*").length,
              iframes: viewerElement.querySelectorAll("iframe").length,
              headers: viewerElement.querySelectorAll("header").length,
              navs: viewerElement.querySelectorAll("nav").length,
            });
          }
        };

        documentViewer.addEventListener("documentLoaded", () => {
          // Mark document as loaded
          isDocumentLoaded.current = true;

          // Process any queued annotations
          if (queuedAnnotations.current) {
            try {
              const { annotationManager: annManager } = instance.Core;
              annManager.importAnnotations(queuedAnnotations.current);
              queuedAnnotations.current = null;
            } catch (error) {
              console.error("Error importing queued annotations:", error);
            }
          }

          // Set total pages - with delay for PPTX files
          const updatePageCount = () => {
            try {
              const pageCount = documentViewer.getPageCount();
              if (pageCount > 0) {
                setTotalPages(pageCount);
                setCurrentPage(1);
                console.log("Page count updated:", pageCount);
              } else {
                // Retry if page count is 0 (common with PPTX)
                setTimeout(updatePageCount, 500);
              }
            } catch (e) {
              console.error("Error getting page count:", e);
              // Retry after delay
              setTimeout(updatePageCount, 500);
            }
          };

          // Try immediately and with delays for PPTX files
          updatePageCount();
          setTimeout(updatePageCount, 100);
          setTimeout(updatePageCount, 500);
          setTimeout(updatePageCount, 1000);

          // Default rectangle annotation removed - no longer creating automatic square
          // const rectangleAnnot = new Annotations.RectangleAnnotation({
          //   PageNumber: 1,
          //   // values are in page coordinates with (0, 0) in the top left
          //   X: 100,
          //   Y: 150,
          //   Width: 200,
          //   Height: 50,
          //   Author: annotationManager.getCurrentUser(),
          // });
          // annotationManager.addAnnotation(rectangleAnnot);
          // // need to draw the annotation otherwise it won't show up until the page is refreshed
          // annotationManager.redrawAnnotation(rectangleAnnot);
          // disable insert edit

          // Hide toolbars when document loads
          setTimeout(() => {
            hideToolbars();
          }, 100);

          // Ensure presentation mode after document loads
          setTimeout(() => {
            try {
              if (instance.UI.setDisplayMode) {
                instance.UI.setDisplayMode(instance.UI.DisplayMode.Continuous);
              }
              // Fit page to window size automatically - use FitWidth on mobile for better viewing
              const fitMode = isMobile
                ? instance.UI.FitMode.FitWidth
                : instance.UI.FitMode.FitPage;
              instance.UI.setFitMode(fitMode);
            } catch (e) {
              console.log("Could not set presentation mode");
            }
          }, 200);
        });
        // Disable UI elements but keep page navigation
        instance.UI.disableElements([
          "documentHeader",
          "header",
          "toolsHeader",
          "toolbar",
          "ribbon",
          "ribbons",
          "toolbarGroup-Edit",
          "toolbarGroup-Annotate",
          "toolbarGroup-Shapes",
          "toolbarGroup-Insert",
          "toolbarGroup-Forms",
          "toolbarGroup-Comment",
          "toolbarGroup-Print",
          "toolbarGroup-Download",
          "toolbarGroup-FillAndSign",
          "toolbarGroup-View",
          "toolbarGroup-Measure",
          "toolbarGroup-Zoom", // Keep zoom disabled, but allow navigation
          "snippingToolButton",
          "cropToolButton",
          "panToolButton",
          "selectToolButton",
          "viewControlsTab",
          "selectButton",
          "menuButton",
          "toolsButton",
          // Keep Navigation and Page controls enabled
        ]);

        // Try multiple methods to hide toolbar
        try {
          // Method 1: Set header items to empty
          if (instance.UI.setHeaderItems) {
            instance.UI.setHeaderItems(() => []);
          }
        } catch (e) {
          console.log("setHeaderItems not available");
        }

        try {
          // Method 2: Try to get and hide header element directly
          const headerElement = instance.UI.getHeader();
          if (headerElement) {
            headerElement.style.display = "none";
            headerElement.style.visibility = "hidden";
            headerElement.style.height = "0";
          }
        } catch (e) {
          console.log("getHeader not available");
        }

        try {
          // Method 3: Try to disable the entire UI
          if (instance.UI.disableFeatures) {
            instance.UI.disableFeatures(["header"]);
          }
        } catch (e) {
          console.log("disableFeatures not available");
        }
        // Set fit mode for presentation - fit page to window size automatically
        // Use FitWidth on mobile for better viewing experience
        const initialFitMode = isMobile
          ? instance.UI.FitMode.FitWidth
          : instance.UI.FitMode.FitPage;
        instance.UI.setFitMode(initialFitMode);
        const pageNumber = documentViewer.getCurrentPage();

        annotationManager.getAnnotationsList().forEach((annot) => {
          if (annot.PageNumber === pageNumber && annot.Color) {
            // Replace oklch (or any color) with safe RGB
            annot.Color = new Annotations.Color(0, 0, 0); // black
          }
        });

        console.log("documentViewer", documentViewer);

        // Listen for page rendering events to update page count (important for PPTX)
        documentViewer.addEventListener("pagesUpdated", () => {
          try {
            const pageCount = documentViewer.getPageCount();
            if (pageCount > 0) {
              setTotalPages(pageCount);
              console.log("Pages updated, new count:", pageCount);
            }
          } catch (e) {
            console.error("Error updating page count on pagesUpdated:", e);
          }
        });

        documentViewer.addEventListener(
          "pageNumberUpdated",
          async (pageNumber) => {
            // Update current page state
            setCurrentPage(pageNumber);

            // Also update total pages in case it wasn't available earlier (PPTX)
            try {
              const pageCount = documentViewer.getPageCount();
              if (pageCount > 0 && pageCount !== totalPages) {
                setTotalPages(pageCount);
              }
            } catch (e) {
              // Ignore errors
            }

            // Don't broadcast if we're applying a remote change
            if (isApplyingRemoteChange.current) {
              return;
            }
            if (meetingRole != "moderator") {
              return;
            }

            console.log("pageNumber Updated - broadcasting", pageNumber);
            publish(
              {
                eventName: "pageNumberUpdated",
                eventData: { pageNumber: pageNumber },
              },
              { persist: true }
            );
          }
        );

        // Scroll (throttle)
        const scrollView = documentViewer.getScrollViewElement();

        scrollView.addEventListener("scroll", async (scroll) => {
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }

          const scrollTop = scrollView.scrollTop; // Y-axis
          const scrollLeft = scrollView.scrollLeft; // X-axis

          // Throttle scroll events to avoid flooding
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }

          scrollTimeoutRef.current = setTimeout(() => {
            if (meetingRole != "moderator") {
              return;
            }
            console.log("scroll Updated - broadcasting", scrollTop, scrollLeft);
            publish(
              {
                eventName: "scrollUpdated",
                eventData: { scrollTop: scrollTop, scrollLeft: scrollLeft },
              },
              { persist: true }
            );
          }, 200); // Throttle to 200ms for better stability
        });

        // Zoom
        documentViewer.addEventListener("zoomUpdated", (zoom) => {
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          if (meetingRole != "moderator") {
            return;
          }
          console.log("zoom Updated - broadcasting", zoom);
          publish(
            {
              eventName: "zoomUpdated",
              eventData: { zoom: zoom },
            },
            { persist: true }
          );
        });

        documentViewer.addEventListener(
          "rotationUpdated",
          (rotation, pageNumber) => {
            console.log("rotationupdate triggered");
            let currentPage = documentViewer.getCurrentPage();
            console.log(`Page ${currentPage} rotated to ${rotation}`);
            if (meetingRole != "moderator") {
              return;
            }
            publish(
              {
                eventName: "rotationUpdated",
                eventData: { rotation: rotation, pageNumber: currentPage },
              },
              { persist: true }
            );
          }
        );

        // Annotations
        annotationManager.addEventListener(
          "annotationChanged",
          async (annotations, action) => {
            if (meetingRole != "moderator") {
              return;
            }
            // Don't broadcast if we're applying a remote change
            if (isApplyingRemoteChange.current) {
              return;
            }
            const serialized = await annotationManager.exportAnnotations({
              links: false,
            });
            console.log("annotation Changed - broadcasting", action);
            publish(
              {
                eventName: "annotationChanged",
                eventData: { annotations: serialized, action: action },
              },
              { persist: true }
            );
          }
        );

        // Layout change
        documentViewer.addEventListener("displayModeUpdated", (displayMode) => {
          console.log("displayModeChanged triggered", displayMode);

          // Ensure presentation mode is maintained
          try {
            if (instance.UI.setDisplayMode) {
              instance.UI.setDisplayMode(instance.UI.DisplayMode.Continuous);
            }
          } catch (e) {
            console.log("Could not set display mode");
          }

          if (meetingRole != "moderator") {
            return;
          }
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          // console.log("layout Changed - broadcasting", layoutMode);
          // publish(
          //   {
          //     eventName: "layoutChanged",
          //     eventData: { layoutMode: layoutMode },
          //   },
          //   { persist: true }
          // );
        });

        // Tool change
        instance.UI.addEventListener("toolModeUpdated", (tool) => {
          if (meetingRole != "moderator") {
            return;
          }
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          console.log("tool Changed - broadcasting", tool);
          publish(
            {
              eventName: "toolModeUpdated",
              eventData: { toolName: tool.name || tool },
            },
            { persist: true }
          );
        });

        // Fit mode
        instance.UI.addEventListener("fitModeChanged", (fitMode) => {
          if (meetingRole != "moderator") {
            return;
          }
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          console.log("fitMode Changed - broadcasting", fitMode);
          publish(
            {
              eventName: "fitModeChanged",
              eventData: { fitMode: fitMode },
            },
            { persist: true }
          );
        });

        instance.UI.addEventListener("fullscreenchange", () => {
          console.log("fullscreenchange");
        });

        instance.UI.setLayoutMode(instance.UI.LayoutMode.Single);

        // Set presentation mode - continuous display
        try {
          instance.UI.setDisplayMode(instance.UI.DisplayMode.Continuous);
        } catch (e) {
          console.log("setDisplayMode not available, using default");
        }

        // Function to update fit mode based on window size
        const updateFitMode = () => {
          try {
            // Use FitWidth on mobile, FitPage on desktop
            const fitMode = isMobile
              ? instance.UI.FitMode.FitWidth
              : instance.UI.FitMode.FitPage;
            instance.UI.setFitMode(fitMode);
            // Force update view to apply fit mode
            documentViewer.updateView();
          } catch (e) {
            console.log("Could not update fit mode");
          }
        };

        // Add window resize listener to auto-fit pages
        const handleResize = () => {
          updateFitMode();
        };
        resizeHandlerRef.current = handleResize;
        window.addEventListener("resize", handleResize);

        instance.UI.exportModularComponents();

        // Hide toolbars immediately and after multiple delays
        hideToolbars();
        setTimeout(hideToolbars, 100);
        setTimeout(hideToolbars, 300);
        setTimeout(hideToolbars, 500);
        setTimeout(hideToolbars, 1000);
        setTimeout(hideToolbars, 2000);

        // Use MutationObserver to hide dynamically added toolbar elements
        const observer = new MutationObserver(() => {
          hideToolbars();
        });

        if (viewer.current) {
          observer.observe(viewer.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class", "data-element"],
          });
          observerRef.current = observer;
        }

        // Update both state and ref for the instance
        webviewerInstanceRef.current = instance;
        setWebViewerInstance(instance);
      });
    });

    // Cleanup function
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // Remove resize listener
      if (resizeHandlerRef.current) {
        window.removeEventListener("resize", resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      // Reset document loaded flag
      isDocumentLoaded.current = false;
      queuedAnnotations.current = null;
    };
  }, [fileUrl]);

  const goToPreviousPage = () => {
    if (webviewerInstanceRef.current && currentPage > 1) {
      const { documentViewer } = webviewerInstanceRef.current.Core;
      documentViewer.setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (webviewerInstanceRef.current && currentPage < totalPages) {
      const { documentViewer } = webviewerInstanceRef.current.Core;
      documentViewer.setCurrentPage(currentPage + 1);
    }
  };

  // Stop event propagation to prevent closing on click
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <div
        className={`fixed ${isMobile ? "top-[12px]" : "top-[8%]"} z-[99999]`}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onContextMenu={stopPropagation}
        style={{
          right: isMobile
            ? "12px"
            : sideBarMode
            ? `${sideBarContainerWidth + 16}px`
            : "16px",
        }}
      >
        <button
          onClick={() => onClosePanel()}
          className={`${
            isMobile ? "p-2" : "p-2"
          } rounded-full bg-white/90 backdrop-blur-sm shadow-lg active:bg-gray-200 hover:bg-gray-100 transition-colors duration-200 cursor-pointer z-[99999] touch-manipulation`}
          aria-label="Close document viewer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-gray-600`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Custom Page Navigation Controls */}
      {meetingRole === "moderator" && totalPages > 0 && (
        <div
          className={`fixed ${
            isMobile ? "bottom-[20px]" : "bottom-[20px]"
          } z-[99999] flex items-center ${
            isMobile ? "gap-2" : "gap-4"
          } bg-black/80 backdrop-blur-sm rounded-full ${
            isMobile ? "px-3 py-1.5" : "px-4 py-2"
          } shadow-lg`}
          style={{
            left: isMobile
              ? "50%"
              : sideBarMode
              ? `calc((100% - ${sideBarContainerWidth}px) / 2)`
              : "50%",
            transform: "translateX(-50%)",
            touchAction: "manipulation", // Better touch handling
          }}
        >
          <button
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
            className={`${
              isMobile ? "p-1.5" : "p-2"
            } rounded-full transition-all duration-200 touch-manipulation ${
              currentPage <= 1
                ? "bg-gray-700 cursor-not-allowed opacity-40"
                : "bg-white/20 active:bg-white/40 cursor-pointer"
            }`}
            aria-label="Previous page"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-white`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>

          <div
            className={`flex items-center ${
              isMobile ? "gap-1" : "gap-2"
            } text-white ${isMobile ? "text-xs" : "text-sm"} font-medium ${
              isMobile ? "px-2" : "px-3"
            }`}
          >
            <span>{currentPage}</span>
            <span className="text-gray-400">/</span>
            <span>{totalPages}</span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className={`${
              isMobile ? "p-1.5" : "p-2"
            } rounded-full transition-all duration-200 touch-manipulation ${
              currentPage >= totalPages
                ? "bg-gray-700 cursor-not-allowed opacity-40"
                : "bg-white/20 active:bg-white/40 cursor-pointer"
            }`}
            aria-label="Next page"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-white`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      )}
      <div
        id="document-viewer"
        className={`fixed ${isMobile ? "top-0 bottom-0" : "top-[8%]"} left-0 ${
          isMobile ? "h-screen" : "h-[calc(100vh-60px)]"
        } z-[999] overflow-hidden`}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onContextMenu={stopPropagation}
        style={{
          pointerEvents: meetingRole !== "moderator" ? "none" : "auto",
          width: isMobile
            ? "100%"
            : sideBarMode
            ? `calc(100% - ${sideBarContainerWidth}px)`
            : "100%",
          touchAction: "pan-y pan-x", // Enable touch scrolling
          WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
        }}
        ref={viewer}
      ></div>
    </>
  );
}
